import React, { useState, useEffect } from "react";
import {
  Bell, Settings, AlertCircle, Search, Filter,
  Plus, Trash2, Edit, CheckCircle, User, Clock,
  Users, Globe, Target, Send, Eye, Copy, Play,
  Pause, Calendar, MessageSquare, BellRing, ChevronLeft, ChevronRight
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import AdminHeader from "../../Headers/AdminHeader";
import NotificationModal from "../FORM MANAGEMENT/modals/NotificationModal";
import CustomNotificationSettings from "./CustomNotificationSettings";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { 
  fetchUserNotifications, 
  deleteUserNotification,
  clearUserNotificationsError,
  setUserNotificationsSearchQuery
} from "../../redux/Slices/UserNotificationSlice";
import { GlobalService } from "../../modules";

const TabButton = ({ active, label, icon: Icon, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
      active 
        ? "bg-blue-100 text-blue-700" 
        : "text-gray-600 hover:bg-gray-100"
    }`}
  >
    <Icon className="h-4 w-4" />
    <span>{label}</span>
  </button>
);

const CustomNotifications = () => {
  const dispatch = useAppDispatch();
  const {
    userNotifications,
    userNotificationsLoading,
    userNotificationsError,
    userNotificationsTotalCount,
    userNotificationsCurrentPage,
    userNotificationsHasNextPage,
    userNotificationsHasPreviousPage,
    userNotificationsSearchQuery
  } = useAppSelector((state) => state.userNotifications);

  const [activeTab, setActiveTab] = useState("notifications");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Load notifications on component mount and when page changes
  useEffect(() => {
    if (activeTab === "notifications") {
      const query = {
        page: currentPage,
        limit: pageSize,
        sort: JSON.stringify([{ orderBy: "createdAt", order: "DESC" }]),
        ...(searchQuery && { search: searchQuery })
      };
      dispatch(fetchUserNotifications(query));
    }
  }, [dispatch, currentPage, pageSize, searchQuery, activeTab]);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery !== userNotificationsSearchQuery) {
        dispatch(setUserNotificationsSearchQuery(searchQuery));
        setCurrentPage(1); // Reset to first page when searching
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, userNotificationsSearchQuery, dispatch]);

  const handleNewNotification = () => {
    setSelectedClient({
      id: "mock-client",
      firstName: "All",
      lastName: "Users"
    });
    setShowNotificationModal(true);
  };

  const handleDeleteNotification = async (notificationId: string) => {
    if (window.confirm("Are you sure you want to delete this notification?")) {
      try {
        await dispatch(deleteUserNotification(notificationId)).unwrap();
        GlobalService.showToast("success", "Success", "Notification deleted successfully");
      } catch (error: any) {
        GlobalService.showToast("error", "Error", error.message || "Failed to delete notification");
      }
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "high":
      case "urgent":
        return "bg-red-100 text-red-700";
      case "normal":
        return "bg-blue-100 text-blue-700";
      case "low":
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case "banner":
        return "bg-green-100 text-green-700";
      case "popup":
        return "bg-yellow-100 text-yellow-700";
      case "in-app":
        return "bg-blue-100 text-blue-700";
      case "email":
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Mock data for templates
  const templates = [/* ... your templates data ... */];

  const UserNotificationCard = ({ notification }) => (
    <Card className="p-4 hover:shadow-md transition-shadow" style={{ textAlign: "left" }}>
      <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
        <div className="w-full">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <h3 className="font-medium">{notification.title}</h3>
            <div className="flex gap-2">
              <Badge variant={undefined} className={`w-fit ${getPriorityColor(notification.priority?.name)}`}>
                {notification.priority?.name || "Normal"}
              </Badge>
              <Badge variant={undefined} className={`w-fit ${getTypeColor(notification.type?.name)}`}>
                {notification.type?.name || "In-App"}
              </Badge>
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-600">{notification.message}</p>
          
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Recipient:</span>
              <div className="flex items-center gap-2 mt-1">
                <User className="h-4 w-4 text-gray-400 shrink-0" />
                <span className="truncate">
                  {notification.user?.firstName} {notification.user?.lastName} ({notification.user?.email})
                </span>
              </div>
            </div>
            <div>
              <span className="text-gray-500">Created:</span>
              <div className="flex items-center gap-2 mt-1">
                <Calendar className="h-4 w-4 text-gray-400 shrink-0" />
                <span className="truncate">{formatDate(notification.createdAt)}</span>
              </div>
            </div>
          </div>

          {notification.expiryTime && (
            <div className="mt-2 text-sm">
              <span className="text-gray-500">Expires:</span>
              <span className="ml-2 text-orange-600">{formatDate(notification.expiryTime)}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2 w-full lg:w-auto justify-end">
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-red-600 sm:px-2"
            onClick={() => handleDeleteNotification(notification.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );

  const PaginationControls = () => {
    const totalPages = Math.ceil((userNotificationsTotalCount || 0) / pageSize);
    
    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-gray-500">
          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, userNotificationsTotalCount || 0)} of {userNotificationsTotalCount || 0} notifications
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!userNotificationsHasPreviousPage || userNotificationsLoading}
            className="flex items-center gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(pageNum)}
                  disabled={userNotificationsLoading}
                  className="w-8 h-8 p-0"
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!userNotificationsHasNextPage || userNotificationsLoading}
            className="flex items-center gap-1"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  const TemplateCard = ({ template }) => (
    <Card className="p-4"  style={{ textAlign: "left" }}>
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="w-full">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <h3 className="font-medium">{template.name}</h3>
            <Badge variant="outline" className="w-fit">
              {template.type}
            </Badge>
          </div>
          <p className="mt-2 text-sm text-gray-600">{template.content}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {template.variables.map(variable => (
              <Badge variant={undefined} key={variable} className="bg-blue-50 text-blue-700">
                {variable}
              </Badge>
            ))}
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto justify-end">
          <Button variant="ghost" size="sm" className="sm:px-2">
            <Copy className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="sm:px-2">
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-red-600 sm:px-2">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "notifications":
        return (
          <div className="space-y-6" style={{ textAlign: "left" }}>
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search notifications..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 w-full sm:w-auto"
                >
                  <Filter className="h-4 w-4" />
                  Filters
                </Button>
              </div>
              <Button 
                className="flex items-center gap-2 w-full sm:w-auto"
                onClick={handleNewNotification}
              >
                <Plus className="h-4 w-4" />
                New Notification
              </Button>
            </div>

            {/* Error Display */}
            {userNotificationsError && (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  {userNotificationsError}
                </AlertDescription>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => dispatch(clearUserNotificationsError())}
                  className="ml-auto"
                >
                  Dismiss
                </Button>
              </Alert>
            )}

            {/* Notifications List */}
            <div className="space-y-4">
              {userNotificationsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-2 text-gray-500">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                    Loading notifications...
                  </div>
                </div>
              ) : userNotifications.length === 0 ? (
                <Card className="p-8 text-center">
                  <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications found</h3>
                  <p className="text-gray-500 mb-4">
                    {searchQuery ? "Try adjusting your search criteria" : "Get started by creating your first notification"}
                  </p>
                  {!searchQuery && (
                    <Button onClick={handleNewNotification}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Notification
                    </Button>
                  )}
                </Card>
              ) : (
                userNotifications.map(notification => (
                  <UserNotificationCard key={notification.id} notification={notification} />
                ))
              )}
            </div>

            {/* Pagination */}
            <PaginationControls />
          </div>
        );

      case "templates":
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
              <div>
                <h2 className="text-lg font-semibold">Notification Templates</h2>
                <p className="text-gray-600 mt-1">Manage reusable notification templates</p>
              </div>
              <Button className="flex items-center gap-2 w-full sm:w-auto">
                <Plus className="h-4 w-4" />
                Add Template
              </Button>
            </div>

            <div className="space-y-4">
              {templates.map(template => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          </div>
        );

      case "settings":
        return <CustomNotificationSettings />;

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50"  style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="lg:pl-64 pb-16 pt-16">
        <div className="px-4 sm:px-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="py-4">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Custom Notifications</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                Create and manage custom notifications and banners
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card className="p-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Notifications</p>
                    <p className="text-xl sm:text-2xl font-semibold">{userNotificationsTotalCount || 0}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                    <Bell className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Current Page</p>
                    <p className="text-xl sm:text-2xl font-semibold">{currentPage}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg shrink-0">
                    <Eye className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Page Size</p>
                    <p className="text-xl sm:text-2xl font-semibold">{pageSize}</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg shrink-0">
                    <Target className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 border-b overflow-x-auto pb-1">
              <TabButton
                active={activeTab === "notifications"}
                label="Notifications"
                icon={Bell}
                onClick={() => setActiveTab("notifications")}
              />
              {/* <TabButton
                active={activeTab === "templates"}
                label="Templates"
                icon={Copy}
                onClick={() => setActiveTab("templates")}
              /> */}
              <TabButton
                active={activeTab === "settings"}
                label="Settings"icon={Settings}
                onClick={() => setActiveTab("settings")}
              />
            </div>

            {/* Tab Content */}
            <div className="min-h-[300px]">
              {renderContent()}
            </div>
          </div>
        </div>
      </main>

      {/* Notification Modal */}
      {showNotificationModal && selectedClient && (
        <NotificationModal
          isOpen={showNotificationModal}
          onClose={() => {
            setShowNotificationModal(false);
            setSelectedClient(null);
          }}
          client={selectedClient}
        />
      )}
    </div>
  );
};

export default CustomNotifications;